import { Component, ElementRef, OnInit, ViewChild } from '@angular/core';
import {FormBuilder, FormControl, FormGroup, Validators} from '@angular/forms';
import {AviationEvent, AviationEventTypeDesc} from '../../shared/models/aviation-event.model';
import {MatDatepickerInputEvent, MatSelectChange, MatSnackBar} from '@angular/material';
import * as MomentTZ from 'moment-timezone';
import { eventAMPM, eventCanadianProvinces, eventCountries, eventHours, eventMinutes, eventUSStates, LOCAL_TZ } from '../../globals';
import {AviationEventsService} from '../../shared/services/aviation-events.service';
import {AirportService} from '../../airport/airport.service';
import {AirportSearchResult} from '../../airport/models/airport-search-result.model';
import {LatLon} from '../../util/geodesy/latlon';
import {GoogleService} from '../../google/google.service';
import {UserService} from '../../shared/user/user.service';
import {UserInfo} from '../../shared/models/user-info.model';
import {Router} from '@angular/router';
import {BusinessManagerService} from '../../shared/user/business-manager.service';
import {GoogleMapsValidationModel} from '../../google/google-maps-validation.model';
import {AddressValidationService} from '../../google/address-validation.service';
import Granularity = GoogleMapsValidationModel.Granularity;
import { Country } from '../../shared/models/country.model';
import { Timezone } from '../../shared/models/timezone.model';

enum  TimeZones {
  ATLANTIC = 'America/Halifax',
  EASTERN = 'America/New_York',
  CENTRAL = 'America/Chicago',
  MOUNTAIN = 'America/Denver',
  PACIFIC = 'America/Los_Angeles',
  HAWAII = 'Pacific/Honolulu',
  ALASKA = 'America/Anchorage'
}

const USTimezones: Timezone[] = [
  { id: 1, countryCode: 'America/Halifax', zoneName: 'ATLANTIC' },
  { id: 2, countryCode: 'America/New_York', zoneName: 'EASTERN' },
  { id: 3, countryCode: 'America/Chicago', zoneName: 'CENTRAL' },
  { id: 4, countryCode: 'America/Denver', zoneName: 'MOUNTAIN' },
  { id: 5, countryCode: 'America/Los_Angeles', zoneName: 'PACIFIC' },
  { id: 6, countryCode: 'Pacific/Honolulu', zoneName: 'HAWAII' },
  { id: 7, countryCode: 'America/Anchorage', zoneName: 'ALASKA' }
];


@Component({
  selector: 'app-event-add-form',
  templateUrl: './event-add-form.component.html',
  styleUrls: ['./event-add-form.component.scss']
})


export class EventAddFormComponent implements OnInit {

    constructor(
      private _fb: FormBuilder,
      private _aviationEventsService: AviationEventsService,
      private _airportService: AirportService,
      private _snackBar: MatSnackBar,
      private _googleService: GoogleService,
      private _userService: UserService,
      private _router: Router,
      private _businessManagerService: BusinessManagerService,
      private _addressValidationService: AddressValidationService
      ) {}

  addEventForm: FormGroup;
  shortEventDescCharCount = 0;
  shortEventDescMaxChars = 200;
  eventTypes: String[];
  eventFormats: String[] = ['Online', 'In-Person', 'Both'];
  timeZones = Object.keys(TimeZones).sort();
  eventCountries = eventCountries;
  // tslint:disable-next-line:max-line-length
  urlRegex = /^((https?|http):\/\/)?(([a-z\d]([a-z\d-]*[a-z\d])?\.)+[a-z]{2,}|localhost)(\/[-a-z\d%_.~+]*)*(\?[;&a-z\d%_.~+=-]*)?(\#[-a-z\d_]*)?$/i;
  startDateMin: Date = new Date();
  endDateMin: Date = this.startDateMin;
  airports: AirportSearchResult[] = [];
  selectedAirport: AirportSearchResult = null;
  eventCoverImage: File;
  additionalPhoto1: File;
  additionalPhoto2: File;
  airportSelectionError = false;
  _userInfo: UserInfo;
  recId: number;
  showAddressFields = true;
  isFlightDeckAdmin  = false;
  isSaving = false;
  eventHours = eventHours;
  eventMinutes = eventMinutes;
  eventAMPM = eventAMPM;
  countries: Country[];
  timezones: Timezone[];
  currentCountry = 'United States';

  @ViewChild('coverPhotoFile') coverPhotoInput: ElementRef;
  @ViewChild('additionalPhoto1File') additionalPhoto1Input: ElementRef;
  @ViewChild('additionalPhoto2File') additionalPhoto2Input: ElementRef;

  async ngOnInit() {
    const localOffset = MomentTZ().tz(LOCAL_TZ).utcOffset();
    const currentTimeZone = Object.values(TimeZones).filter(it => MomentTZ().tz(it).utcOffset() === localOffset).pop();
    const currentTimeZoneSimpleName =  Object.keys(TimeZones).filter(it => TimeZones[it] === currentTimeZone).pop();
    this.eventTypes = Object.values(AviationEventTypeDesc) as String[];
    this._userInfo = this._userService.userInfo.getValue();
    this._businessManagerService.isFlightDeckAdmin().then((isAdmin) => this.isFlightDeckAdmin = isAdmin);
    if (this._userInfo) {
      this.recId = Number(this._userInfo.masterCustomerId);
    }
    this._userService.userInfo.subscribe(u => {
      // Update userInfo
      this._userInfo = u;
      if (u) {
        this.recId = Number(u.masterCustomerId);
      }
    });
    this.addEventForm = this._fb.group({
      title: ['', Validators.required],
      shortDescription: ['', Validators.required],
      description: ['', Validators.required],
      type: ['', Validators.required],
      format: ['', Validators.required],
      webinarRegistrationLink: [null, Validators.pattern(this.urlRegex)],
      informationWebsite: [null, Validators.pattern(this.urlRegex)],
      websiteCustomLabel: ['', Validators.nullValidator],
      cost: ['', Validators.nullValidator],
      coverPhoto: ['', [this.requiredFileType(['jpg', 'png', 'jpeg', 'gif'])]],
      additionalPhoto1: [{value: '', disabled: true}, [this.requiredFileType(['jpg', 'png', 'jpeg', 'gif'])]],
      additionalPhoto2: [{value: '', disabled: true}, [this.requiredFileType(['jpg', 'png', 'jpeg', 'gif'])]],
      startDate: ['', Validators.required],
      endDate: ['', Validators.required],
      startHour: ['', Validators.required],
      startMin: ['', Validators.required],
      startAMPM: ['', Validators.required],
      endHour: ['', Validators.required],
      endMin: ['', Validators.required],
      endAMPM: ['', Validators.required],
      timezone: ['', Validators.required],
      venueLocationName: ['', Validators.required],
      address1: ['', Validators.required],
      address2: ['', Validators.nullValidator],
      address3: ['', Validators.nullValidator],
      city: ['', Validators.required],
      isOnAirport: [false, Validators.nullValidator],
      airport: ['', Validators.nullValidator],
      stateProvince: ['', Validators.required],
      zipcode: ['', Validators.required],
      country: ['US', Validators.required],
      internationalAirportId: ['', Validators.nullValidator],
      internationalAirportName: ['', Validators.nullValidator],
      contactPersonName: ['', Validators.required],
      contactPersonEmail: ['', Validators.required],
      contactPersonPhone: ['', Validators.required],
      isValidated: ['', Validators.nullValidator],
      isFeatured: ['', Validators.nullValidator],
      hasAopaPresence: ['', Validators.nullValidator],
      isAopaHosted: ['', Validators.nullValidator],
      latitude: ['', [Validators.min(-90), Validators.max(90), Validators.pattern('^-?\\d+[.]\\d+$')]],
      longitude: ['', [Validators.min(-180), Validators.max(180), Validators.pattern('^-?\\d+[.]\\d+$')]]
    }, {updateOn: 'submit'});
    this.countries = await this._aviationEventsService.getCountries();
    await this.updateTimeZones('US');
  }

  private buildZonedTime(date: Date, time: string, timezone: String): string {
    const month = ('0' + (date.getMonth() + 1)).slice(-2);
    const day = ('0' + date.getDate()).slice(-2);
    const year = date.getFullYear();
    const localTime = MomentTZ.tz(`${year}-${month}-${day} ${time}`, timezone);
    return localTime.utc().format('MM/DD/YYYY h:mm:ss A');
  }

  onSubmit(form: FormGroup) {
    const formValue = this.addEventForm.value;
    const eventURL = 'event/';
    if (formValue.airport.trim() && this.selectedAirport == null ) {
      this.airportSelectionError = true;
      this._snackBar.open('Invalid Airport Selected', null, {duration: 3000});
      return;
    } else if (this.addEventForm.invalid) {
      this._snackBar.open('Form field validation failed. check form values', null, {duration: 3000});
      return;
    } else if (this.photoIsTooLarge('coverPhoto')) {
      this._snackBar.open('Cover Photo/Thumbnail file is too Large: File must be below 10MB', null, {duration: 5000});
      return;
    } else if (this.photoIsTooLarge('additionalPhoto1')) {
      this._snackBar.open('Additional Photo 1 file is too Large: File must be below 10MB', null, {duration: 5000});
      return;
    } else if (this.photoIsTooLarge('additionalPhoto2')) {
      this._snackBar.open('Additional Photo 2 file is too Large: File must be below 10MB', null, {duration: 5000});
      return;
    } else if (this.showDateError()) {
      this._snackBar.open('start time cannot be after end time, check end time is not before start time', null, {duration: 3000});
      return;
    }

    this.isSaving = true;
    this._getAviationEventFromFormValue(formValue)
      .then( event => this._aviationEventsService.addAviationEvent(event))
      .then( data => {
        if (this.eventCoverImage) {
          this._aviationEventsService.uploadEventCoverImg(this.eventCoverImage, data.eventId).then(photoRes => {
            if (this.additionalPhoto1) {
              this._aviationEventsService.uploadEventCoverImg(this.additionalPhoto1, data.eventId).then(photoRes1 => {
                if (this.additionalPhoto2) {
                  this._aviationEventsService.uploadEventCoverImg(this.additionalPhoto2, data.eventId).then(photoRes2 => {
                    this.isSaving = false;
                    return this._router.navigateByUrl(eventURL + data.eventId);
                  });
                } else {
                  this.isSaving = false;
                  return this._router.navigateByUrl(eventURL + data.eventId);
                }
              });
            } else {
              this.isSaving = false;
              return this._router.navigateByUrl(eventURL + data.eventId);
            }
          });
        } else {
          this.isSaving = false;
          return this._router.navigateByUrl(eventURL + data.eventId);
        }
      }).catch(err => {
        console.error(err);
        this.isSaving = false;
    });
  }

  /* public isGooglePlacesCountry(): boolean {
    if (this.addEventForm.value.country && eventGoogleCountries.includes(this.addEventForm.value.country)) {
      return true;
    }
    return false;
  } */

  public isUSorCA(): boolean {
    if (this.addEventForm.value.country && ['US', 'CA'].includes(this.addEventForm.value.country)) {
      return true;
    }
    return false;
  }

  private _getRegionCodeFromCountry(country: string) {
    switch (country.toLowerCase()) {
      /* case 'argentina':
        return 'AR';
      case 'austria':
        return 'AT';
      case 'australia':
        return 'AU';
      case 'belgium':
        return 'BE';
      case 'bulgaria':
        return 'BG';
      case 'brazil':
        return 'BR';
      case 'switzerland':
        return 'CH';
      case 'chile':
        return 'CL';
      case 'colombia':
        return 'CO';
      case 'czechia':
        return 'CZ';
      case 'germany':
        return 'DE';
      case 'denmark':
        return 'DK';
      case 'estonia':
        return 'EE';
      case 'spain':
        return 'ES';
      case 'finland':
        return 'FI';
      case 'france':
        return 'FR';
      case 'united kingdom':
        return 'GB';
      case 'croatia':
        return 'HR';
      case 'hungary':
        return 'HU';
      case 'ireland':
        return 'IE';
      case 'india':
        return 'IN';
      case 'italy':
        return 'IT';
      case 'lithuania':
        return 'LT';
      case 'luxembourg':
        return 'LU';
      case 'latvia':
        return 'LV';
      case 'mexico':
        return 'MX';
      case 'Malaysia':
        return 'MY';
      case 'netherlands':
        return 'NL';
      case 'norway':
        return 'NO';
      case 'new zealand':
        return 'NZ';
      case 'poland':
        return 'PL';
      case 'puerto rico':
        return 'PR';
      case 'portugal':
        return 'PT';
      case 'sweden':
        return 'SE';
      case 'singapore':
        return 'SG';
      case 'slovenia':
        return 'SI';
      case 'slovakia':
        return 'SK'; */
      case 'us':
        return 'US';
      case 'ca':
        return 'CA';
      default:
        return null;
    }
  }

  private async _getAviationEventFromFormValue(formValue: any): Promise<AviationEvent> {
    const aviationEvent = new AviationEvent();
    let tzName = formValue.timezone;
    if (formValue.country === 'US') {
      tzName = USTimezones.find(i => i.zoneName === formValue.timezone).countryCode;
    }
    const startDate = new Date(formValue.startDate);
    let startTime = +formValue.startHour < 10 ? '0' + formValue.startHour : formValue.startHour;
    // if startTime is 12:00 AM, set to 00:00
    if (+formValue.startHour === 12 && formValue.startAMPM === 'AM') {
      startTime = '00';
    } else {
      startTime = formValue.startAMPM === 'PM' && +formValue.startHour < 12 ? (+startTime + 12).toString() :
        startTime;
    }
    startTime = startTime + ':' + formValue.startMin;
    const endDate = new Date(formValue.endDate);
    let endTime = +formValue.endHour < 10 ? '0' + formValue.endHour : formValue.endHour;
    // if endTime is 12:00 AM, set to 00:00
    if (+formValue.endHour === 12 && formValue.endAMPM === 'AM') {
      endTime = '00';
    } else {
      endTime = formValue.endAMPM === 'PM' && +formValue.endHour < 12 ? (+endTime + 12).toString() : endTime;
    }
    endTime = endTime + ':' + formValue.endMin;
    let postalAddress: GoogleMapsValidationModel.PostalAddress = null;
    const regionCode = this._getRegionCodeFromCountry(formValue.country);
    aviationEvent.recid = this.recId;
    aviationEvent.title = formValue.title;
    aviationEvent.shortDescription = formValue.shortDescription;
    aviationEvent.description = formValue.description;
    aviationEvent.type = AviationEvent.parseEventFromString(formValue.type);
    aviationEvent.typeId = aviationEvent.type;
    aviationEvent.webinarRegistrationLink = formValue.webinarRegistrationLink;
    aviationEvent.websiteCustomLabel = formValue.websiteCustomLabel;
    aviationEvent.cost = formValue.cost && formValue.cost.trim() ? formValue.cost.trim() : null;
    aviationEvent.timezone = tzName;
    aviationEvent.startDateTimeUTC = this.buildZonedTime(startDate, startTime, tzName);
    aviationEvent.endDateTimeUTC = this.buildZonedTime(endDate, endTime, tzName);
    aviationEvent.venueLocationName = formValue.venueLocationName;
    aviationEvent.address1 = formValue.address1;
    aviationEvent.address2 = formValue.address2 && formValue.address2.trim();
    aviationEvent.address3 = formValue.address3 && formValue.address3.trim();
    aviationEvent.city = formValue.city;
    aviationEvent.stateProvince = formValue.stateProvince;
    aviationEvent.zipcode = formValue.zipcode;
    aviationEvent.country = this.currentCountry;
    aviationEvent.countryCode = formValue.country;
    aviationEvent.airportId = this.selectedAirport ? this.selectedAirport.airportId : null;
    aviationEvent.contactPersonName = formValue.contactPersonName;
    aviationEvent.contactPersonEmail = formValue.contactPersonEmail;
    aviationEvent.contactPersonPhone = formValue.contactPersonPhone;
    aviationEvent.isValidated = formValue.isValidated || false;
    aviationEvent.isFeatured = formValue.isFeatured || false;
    aviationEvent.hasAopaPresence = formValue.hasAopaPresence || false;
    aviationEvent.isAopaHosted = formValue.isAopaHosted || false;
    aviationEvent.isVisible = true;
    aviationEvent.format = formValue.format;
    aviationEvent.informationWebsite = formValue.informationWebsite;
    aviationEvent.internationalAirportId = formValue.internationalAirportId;
    aviationEvent.internationalAirportName = formValue.internationalAirportName;

    if (regionCode && formValue.address1 && formValue.city && formValue.stateProvince && formValue.zipcode && formValue.country) {
        // US and Canada addresses
        postalAddress = {
          regionCode: regionCode,
          languageCode: 'EN',
          postalCode: aviationEvent.zipcode,
          locality: aviationEvent.city,
          addressLines: [aviationEvent.address1],
        };
      /* } else {
        // International addresses that Google supports
        postalAddress = {
          regionCode: regionCode,
          languageCode: 'EN',
          addressLines: [aviationEvent.address1, aviationEvent.address2, aviationEvent.address3],
        };
      } */

      const validationResponse  = await this._addressValidationService
        .checkAddress(new GoogleMapsValidationModel.RequestBody(postalAddress));
      const result = validationResponse.result;
      const verdict = result.verdict;
      const granularityIsCorrect = verdict.geocodeGranularity === Granularity.PREMISE ||
        verdict.geocodeGranularity === Granularity.SUB_PREMISE;

      if (!verdict) {
        return aviationEvent;
      }

      if (granularityIsCorrect && !verdict.hasUnconfirmedComponents) {
        const address = result.address.postalAddress;
        if (regionCode === 'US' || regionCode === 'CA') {
          aviationEvent.city = address.locality;
          aviationEvent.zipcode = address.postalCode.split('-')[0];
          aviationEvent.address1 = address.addressLines[0];
          aviationEvent.stateProvince = address.administrativeArea;
        } else {
          aviationEvent.address1 = address.addressLines[0];
          if (address.addressLines.length > 1) {
            aviationEvent.address2 = address.addressLines[1];
          }
          if (address.addressLines.length > 2) {
            aviationEvent.address3 = address.addressLines[2];
          }
          if (address.locality) {
            aviationEvent.city = address.locality;
          }
          if (address.postalCode) {
            aviationEvent.zipcode = address.postalCode;
          }
        }
        const location =
          result.geocode && result.geocode.location ?
            result.geocode.location : null;
        aviationEvent.location = new LatLon(location.latitude, location.longitude);
        aviationEvent.latitude = location.latitude;
        aviationEvent.longitude = location.longitude;
      } else {
        const errorMsg = 'Invalid Address Enter a Correct Address';
        if (verdict.hasUnconfirmedComponents && result.address.missingComponentTypes) {
          this._snackBar
            .open( `${errorMsg}, check: ${result.address.missingComponentTypes.join(', ')}`, null, {duration: 3000});
        } else {
          this._snackBar.open(errorMsg, null, {duration: 3000});
        }
        throw new Error('Invalid Address');
      }
    } else {
      // International addresses that Google doesn't support
      // aviationEvent.city = address.locality;
      // aviationEvent.zipcode = address.postalCode.split('-')[0];
      aviationEvent.address1 = formValue.address1;
      aviationEvent.address2 = formValue.address2;
      aviationEvent.address3 = formValue.address3;
      aviationEvent.location = new LatLon(formValue.latitude, formValue.longitude);
      aviationEvent.latitude = formValue.latitude;
      aviationEvent.longitude = formValue.longitude;
    }
    return aviationEvent;
  }

  eventDateChange(type: string, event: MatDatepickerInputEvent<Date>) {
    const element = event.targetElement;
    if (element.getAttribute('name') === 'startDate') {
      this.endDateMin = event.value;
    }
  }

  webinarRegistrationKeyUp(event: KeyboardEvent) {
    const val = (event.target as HTMLInputElement).value;
    if (val.length < 3) {
      return;
    }
    this.addEventForm.get('webinarRegistrationLink').setValue(val.trim());
  }

  informationWebsiteKeyUP(event: KeyboardEvent) {
    const val = (event.target as HTMLInputElement).value;
    if (val.length < 3) {
      return;
    }
    this.addEventForm.get('informationWebsite').setValue(val.trim());
  }

  airportQueryKeyUp(event:  KeyboardEvent) {
    const val = (event.target as HTMLInputElement).value;
    if (val.length < 3) {
      if (!val || val.length === 0) {
        this.airportSelectionError = false;
        this.selectedAirport = null;
        this.airports = [];
      }
      return;
    }
    this._airportService.getAirportsBySearchTerm(val, true).then((res) => {
      this.airports = res;
    }).catch(err => console.error(err));
  }
  cancelButtonClicked(event: MouseEvent) {
    this._router.navigate(['/'], { queryParams: { 't': 'events' } });
  }

  onPhotoCoverChange(event) {
    if (event.target.files && event.target.files.length) {
      this.eventCoverImage = event.target.files[0];
      this.addEventForm.controls['additionalPhoto1'].enable();
    } else {
      this.eventCoverImage = null;
      this.additionalPhoto1 = null;
      this.addEventForm.controls['additionalPhoto1'].disable();
      this.additionalPhoto1Input.nativeElement.value = '';
      this.additionalPhoto2 = null;
      this.addEventForm.controls['additionalPhoto2'].disable();
      this.additionalPhoto2Input.nativeElement.value = '';
    }
  }

  onAdditionalPhoto1Change(event) {
    if (event.target.files && event.target.files.length) {
      this.additionalPhoto1 = event.target.files[0];
      this.addEventForm.controls['additionalPhoto2'].enable();
    } else {
      this.additionalPhoto1 = null;
      this.additionalPhoto2 = null;
      this.addEventForm.controls['additionalPhoto2'].disable();
      this.additionalPhoto2Input.nativeElement.value = '';
    }
  }

  onAdditionalPhoto2Change(event) {
    if (event.target.files && event.target.files.length) {
      this.additionalPhoto2 = event.target.files[0];
    } else {
      this.additionalPhoto2 = null;
    }
  }

  photoIsTooLarge(photoType: string) {
    if (photoType === 'additionalPhoto1') {
      return this.additionalPhoto1 && this.additionalPhoto1.size > 10000000;
    } else if (photoType === 'additionalPhoto2') {
      return this.additionalPhoto2 && this.additionalPhoto2.size > 10000000;
    } else {
      return this.eventCoverImage && this.eventCoverImage.size > 10000000;
    }
  }

  hasInvalidFileType(photoType: string) {
    return this.addEventForm.get(photoType).hasError('requiredFileType');
  }

  showDateError(): boolean {
    const startDate: Date = this.addEventForm.value.startDate;
    const endDate: Date = this.addEventForm.value.endDate;
    let startHour: number = +this.addEventForm.value.startHour;
    if (startHour === 12) {
      startHour = 0;
    }
    startHour = this.addEventForm.value.startAMPM === 'PM' ? startHour + 12 : startHour;
    let endHour: number = +this.addEventForm.value.endHour;
    if (endHour === 12) {
      endHour = 0;
    }
    endHour = this.addEventForm.value.endAMPM === 'PM' ? endHour + 12 : endHour;

    const dateBoolean = startDate && endDate && startDate.getTime() > endDate.getTime();
    if (startDate && endDate && startDate.getTime() === endDate.getTime()) {
      return startHour > endHour || (startHour === endHour && +this.addEventForm.value.startMin > +this.addEventForm.value.endMin);
    }
    return dateBoolean;
  }

  requiredFileType(types: string[]) {
    return function (control: FormControl) {
      const fileName = control.value;
      if (fileName) {
        const extension = fileName.split('.').pop().toLowerCase();
        let hasExtension = false;
        types.forEach(it => {
          if (it.toLowerCase() === extension.toLowerCase()) {
            hasExtension = true;
            return;
          }
        });
        if (!hasExtension) {
          return {
            requiredFileType: true
          };
        }
      }
    };
  }

  shortEventKeyUp(event: KeyboardEvent) {
    const val = (event.target as HTMLInputElement).value;
    this.shortEventDescCharCount = val.length;
  }

  getStates(): String[] {
    const COUNTRY: String = this.addEventForm.value.country;
    let resultArry: String[] = [];
    if (COUNTRY === 'US') {
      resultArry = eventUSStates;
    } else if (COUNTRY === 'CA') {
      resultArry = eventCanadianProvinces;
    }
    return resultArry;
  }

  async statesFieldChanged(event: MatSelectChange) {
    this.addEventForm.patchValue({stateProvince: ''});
    this.addEventForm.patchValue({city: ''});
    this.addEventForm.patchValue({zipcode: ''});
    this.addEventForm.patchValue({address3: ''});
    this.addEventForm.patchValue({internationalAirportId: ''});
    this.addEventForm.patchValue({internationalAirportName: ''});
    this.addEventForm.patchValue({airport: ''});
    this.selectedAirport = null;
    if (['US', 'CA'].includes(this.addEventForm.value.country)) {
      this.addEventForm.controls['city'].setValidators(Validators.required);
      this.addEventForm.controls['stateProvince'].setValidators(Validators.required);
      this.addEventForm.controls['zipcode'].setValidators(Validators.required);
      this.addEventForm.controls['latitude'].setValidators([]);
      this.addEventForm.controls['longitude'].setValidators([]);
    } else {
      this.addEventForm.controls['city'].setValidators([]);
      this.addEventForm.controls['stateProvince'].setValidators([]);
      this.addEventForm.controls['zipcode'].setValidators([]);
      this.addEventForm.controls['latitude'].setValidators([Validators.required, Validators.min(-90), Validators.max(90),
        Validators.pattern('^-?\\d+[.]\\d+$')]);
      this.addEventForm.controls['longitude'].setValidators([Validators.required, Validators.min(-180), Validators.max(180),
        Validators.pattern('^-?\\d+[.]\\d+$')]);
    }
    this.addEventForm.controls['city'].updateValueAndValidity();
    this.addEventForm.controls['stateProvince'].updateValueAndValidity();
    this.addEventForm.controls['zipcode'].updateValueAndValidity();
    this.addEventForm.controls['latitude'].updateValueAndValidity();
    this.addEventForm.controls['longitude'].updateValueAndValidity();
    this.currentCountry = event.source.triggerValue;
    await this.updateTimeZones(event.value);
  }

  async updateTimeZones(countryCode: string) {
    if (countryCode === 'US') {
      this.timezones = USTimezones;
      this.addEventForm.patchValue({timezone: ''});
    } else {
      this.timezones = await this._aviationEventsService.getTimezones(countryCode);
      if (this.timezones.length === 1) {
        this.addEventForm.patchValue({timezone: this.timezones[0].zoneName});
      } else {
        this.addEventForm.patchValue({timezone: ''});
      }
    }
  }

  airportChange(airport: AirportSearchResult) {
    this.selectedAirport = airport;
    this.airportSelectionError = false;
  }

  airportSearchFocusOut(event: Event) {
    if (this.selectedAirport) {
      this.addEventForm.patchValue({airport: `${this.selectedAirport.identifier}: ${this.selectedAirport.name}`});
    } else {
      this.addEventForm.patchValue({airport: ''});
    }
  }

  eventFormatSelectionChange (event: MatSelectChange) {
    const format = event.value;
    if (format === 'Online') {
      this._setAddressValidation(false);
      this.showAddressFields = false;
      this.addEventForm.patchValue({address1: ''});
      this.addEventForm.patchValue({address2: ''});
      this.addEventForm.patchValue({address3: ''});
      this.addEventForm.patchValue({city: ''});
      this.addEventForm.patchValue({state: ''});
      this.addEventForm.patchValue({zipcode: ''});
      this.addEventForm.patchValue({internationalAirportId: ''});
      this.addEventForm.patchValue({internationalAirportName: ''});
      this.addEventForm.patchValue({latitude: ''});
      this.addEventForm.patchValue({longitude: ''});
    } else {
      this._setAddressValidation();
      // this.addEventForm.patchValue({country: 'US'});
      this.showAddressFields = true;
    }
  }

  private _setAddressValidation(validationIsRequired: Boolean = true) {
    this.addEventForm.removeControl('address1');
    this.addEventForm.removeControl('stateProvince');
    this.addEventForm.removeControl('city');
    this.addEventForm.removeControl('zipcode');
    // this.addEventForm.removeControl('country');
    this.addEventForm.removeControl('venueLocationName');
    if (validationIsRequired) {
      this.addEventForm.addControl('address1', new FormControl('', Validators.required));
      if (['US', 'CA'].includes(this.addEventForm.value.country)) {
        this.addEventForm.addControl('stateProvince', new FormControl('', Validators.required));
        this.addEventForm.addControl('city', new FormControl('', Validators.required));
        this.addEventForm.addControl('zipcode', new FormControl('', Validators.required));
        this.addEventForm.addControl('latitude', new FormControl('', Validators.nullValidator));
        this.addEventForm.addControl('longitude', new FormControl('', Validators.nullValidator));
      } else {
        this.addEventForm.addControl('stateProvince', new FormControl('', Validators.nullValidator));
        this.addEventForm.addControl('city', new FormControl('', Validators.nullValidator));
        this.addEventForm.addControl('zipcode', new FormControl('', Validators.nullValidator));
        this.addEventForm.addControl('latitude', new FormControl('', [Validators.required,
          Validators.min(-90), Validators.max(90), Validators.pattern('^-?\\d+[.]\\d+$')]));
        this.addEventForm.addControl('longitude', new FormControl('', [Validators.required,
          Validators.min(-180), Validators.max(180), Validators.pattern('^-?\\d+[.]\\d+$')]));
      }
      // this.addEventForm.addControl('country', new FormControl('', Validators.required));
      this.addEventForm.addControl('venueLocationName', new FormControl('', Validators.required));
    } else {
      this.addEventForm.addControl('address1', new FormControl('', Validators.nullValidator));
      this.addEventForm.addControl('stateProvince', new FormControl('', Validators.nullValidator));
      this.addEventForm.addControl('city', new FormControl('', Validators.nullValidator));
      this.addEventForm.addControl('zipcode', new FormControl('', Validators.nullValidator));
      // this.addEventForm.addControl('country', new FormControl('', Validators.nullValidator));
      this.addEventForm.addControl('venueLocationName', new FormControl('', Validators.nullValidator));
    }

  }
}
